import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  requireNativeComponent,
  UIManager,
  findNodeHandle,
  ViewStyle,
  ViewProps,
  NativeModules,
} from 'react-native';

export interface DocumentInfo {
  title?: string;
  author?: string;
  documentType: string;
  totalPages: number;
  currentPage: number;
}

export interface FieldcraftDocumentReaderProps extends ViewProps {
  source: string;
  documentType?: 'pdf' | 'doc' | 'docx' | 'markdown' | 'text' | 'html' | 'rtf';
  pagesPerRequest?: number;
  maxZoom?: number;
  minZoom?: number;
  initialPage?: number;
  spacing?: number;
  preloadPages?: number;
  maxDisplayWidth?: number;
  systemMargin?: number;
  enableWidthFit?: boolean;
  style?: ViewStyle;
  
  // Event callbacks
  onPagesLoaded?: (event: any) => void;
  onPageChanged?: (event: any) => void;
  onZoomChanged?: (event: any) => void;
  onDocumentLoaded?: (event: any) => void;
  onLoadProgress?: (event: any) => void;
  onError?: (event: any) => void;
  onLoadingStateChanged?: (event: any) => void;
}

interface NativeFieldcraftDocumentReaderProps extends FieldcraftDocumentReaderProps {
  // All props are the same, RCTConvert will handle the string to enum conversion
}

export interface FieldcraftDocumentReaderRef {
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  setZoom: (scale: number) => void;
  getCurrentPage: () => Promise<number>;
  getTotalPages: () => Promise<number>;
  getDocumentInfo: () => Promise<DocumentInfo>;
  extractTextFromPage: (page: number) => Promise<string>;
  extractTextFromRange: (startPage: number, endPage: number) => Promise<string>;
  extractTextFromPages: (pages: number[]) => Promise<string>;
  searchInDocument: (query: string, options?: any) => Promise<any>;
}

const COMPONENT_NAME = "FieldcraftDocumentReader";
const NativeFieldcraftDocumentReaderView = requireNativeComponent<NativeFieldcraftDocumentReaderProps>(COMPONENT_NAME);

// Standard React Native component - uses UIManager commands for both platforms

export const FieldcraftDocumentReader = forwardRef<
  FieldcraftDocumentReaderRef,
  FieldcraftDocumentReaderProps
>((props, ref) => {
  const viewRef = useRef<any>(null);
  const [isComponentReady, setIsComponentReady] = React.useState(false);
  const [isDocumentLoaded, setIsDocumentLoaded] = React.useState(false);

  const dispatchCommand = (command: string, args: any[] = []) => {
    console.log('📚 [COMMAND] Dispatching:', command, 'args:', args);
    
    if (!viewRef.current) {
      console.error('❌ [COMMAND] Cannot dispatch - component ref is null:', command);
      return;
    }

    const node = findNodeHandle(viewRef.current);
    if (!node) {
      console.error('❌ [COMMAND] Cannot dispatch - no view node found for command:', command);
      return;
    }

    try {
      console.log('📚 [COMMAND] Dispatching view manager command:', command);
      
      // Get the ViewManager config to access the command map
      const viewManagerConfig = UIManager.getViewManagerConfig('FieldcraftDocumentReader');
      if (viewManagerConfig && viewManagerConfig.Commands && viewManagerConfig.Commands[command] !== undefined) {
        // Use the command ID from the map
        const commandId = viewManagerConfig.Commands[command];
        console.log('📚 [COMMAND] Using command ID:', commandId, 'for command:', command);
        UIManager.dispatchViewManagerCommand(node, commandId, args);
      } else {
        // Fallback to direct command name (for commands that don't have promise signatures)
        console.log('📚 [COMMAND] Using direct command name:', command);
        UIManager.dispatchViewManagerCommand(node, command, args);
      }
      
    } catch (error) {
      console.error('❌ [COMMAND] Error dispatching command:', command, error);
    }
  };

  // Performance optimization: debounce rapid command calls
  const commandDebounceRef = React.useRef<{ [key: string]: number }>({});
  const COMMAND_DEBOUNCE_MS = 100;

  const debouncedDispatchCommand = React.useCallback((command: string, args: any[] = []) => {
    const now = Date.now();
    const lastCall = commandDebounceRef.current[command] || 0;
    
    if (now - lastCall < COMMAND_DEBOUNCE_MS) {
      console.log('🚀 [PERF] Command debounced:', command, 'last call:', now - lastCall, 'ms ago');
      return;
    }
    
    commandDebounceRef.current[command] = now;
    dispatchCommand(command, args);
  }, []);

  useImperativeHandle(ref, () => ({
    goToPage: (page: number) => {
      console.log('📚 goToPage called with page:', page);
      if (typeof page !== 'number' || page < 1) {
        console.error('❌ Invalid page number for goToPage:', page);
        return;
      }
      dispatchCommand("goToPage", [page]);
    },
    
    nextPage: () => {
      console.log('📚 nextPage called');
      // Use NativeModules directly for nextPage since it has promise signature
      if (viewRef.current) {
        const node = findNodeHandle(viewRef.current);
        if (node) {
          const FieldcraftDocumentReaderModule = NativeModules.FieldcraftDocumentReader;
          if (FieldcraftDocumentReaderModule && FieldcraftDocumentReaderModule.nextPage) {
            FieldcraftDocumentReaderModule.nextPage(node)
              .then((success: boolean) => {
                console.log('📚 nextPage result:', success);
              })
              .catch((error: any) => {
                console.error('📚 nextPage error:', error);
              });
          } else {
            console.error('📚 FieldcraftDocumentReader module not available for nextPage');
          }
        }
      }
    },
    
    prevPage: () => {
      console.log('📚 prevPage called');
      // Use NativeModules directly for prevPage since it has promise signature
      if (viewRef.current) {
        const node = findNodeHandle(viewRef.current);
        if (node) {
          const FieldcraftDocumentReaderModule = NativeModules.FieldcraftDocumentReader;
          if (FieldcraftDocumentReaderModule && FieldcraftDocumentReaderModule.prevPage) {
            FieldcraftDocumentReaderModule.prevPage(node)
              .then((success: boolean) => {
                console.log('📚 prevPage result:', success);
              })
              .catch((error: any) => {
                console.error('📚 prevPage error:', error);
              });
          } else {
            console.error('📚 FieldcraftDocumentReader module not available for prevPage');
          }
        }
      }
    },
    
    zoomIn: () => {
      console.log('📚 zoomIn called');
      debouncedDispatchCommand("zoomIn");
    },
    
    zoomOut: () => {
      console.log('📚 zoomOut called');
      debouncedDispatchCommand("zoomOut");
    },
    
    setZoom: (scale: number) => {
      console.log('📚 setZoom called with scale:', scale);
      if (typeof scale !== 'number' || scale <= 0) {
        console.error('❌ Invalid zoom scale for setZoom:', scale);
        return;
      }
      dispatchCommand("setZoom", [scale]);
    },
    
    // These methods would need separate NativeModules for return values
    // For now, return promises that resolve to placeholder values
    getCurrentPage: () => Promise.resolve(1),
    getTotalPages: () => Promise.resolve(0),
    getDocumentInfo: () => Promise.resolve({
      title: "PDF Document",
      author: "Unknown",
      documentType: "pdf",
      totalPages: 0,
      currentPage: 1
    }),
    extractTextFromPage: (page: number) => {
      return new Promise((resolve, reject) => {
        if (!viewRef.current) {
          reject(new Error('View not found'));
          return;
        }
        const node = findNodeHandle(viewRef.current);
        if (!node) {
          reject(new Error('View node not found'));
          return;
        }
        const FieldcraftDocumentReaderModule = NativeModules.FieldcraftDocumentReader;
        FieldcraftDocumentReaderModule.extractTextFromPage(node, page)
          .then(resolve)
          .catch(reject);
      });
    },
    extractTextFromRange: (startPage: number, endPage: number) => {
      return new Promise((resolve, reject) => {
        if (!viewRef.current) {
          reject(new Error('View not found'));
          return;
        }
        const node = findNodeHandle(viewRef.current);
        if (!node) {
          reject(new Error('View node not found'));
          return;
        }
        const FieldcraftDocumentReaderModule = NativeModules.FieldcraftDocumentReader;
        FieldcraftDocumentReaderModule.extractTextFromRange(node, startPage, endPage)
          .then(resolve)
          .catch(reject);
      });
    },
    extractTextFromPages: (pages: number[]) => {
      return new Promise((resolve, reject) => {
        if (!viewRef.current) {
          reject(new Error('View not found'));
          return;
        }
        const node = findNodeHandle(viewRef.current);
        if (!node) {
          reject(new Error('View node not found'));
          return;
        }
        const FieldcraftDocumentReaderModule = NativeModules.FieldcraftDocumentReader;
        FieldcraftDocumentReaderModule.extractTextFromPages(node, pages)
          .then(resolve)
          .catch(reject);
      });
    },
    searchInDocument: (_query: string, _options?: any) => Promise.resolve([]),
  }));

  // Component lifecycle management
  React.useEffect(() => {
    console.log('📚 FieldcraftDocumentReader mounted');
    setIsComponentReady(true);
    
    return () => {
      console.log('📚 FieldcraftDocumentReader unmounting');
      setIsComponentReady(false);
      setIsDocumentLoaded(false);
    };
  }, []);



  // Enhanced event handlers with state tracking
  const handleDocumentLoaded = (event: any) => {
    const timestamp = Date.now();
    console.log('📚 [EVENT] Document loaded:', {
      timestamp,
      nativeEvent: event.nativeEvent,
      previousState: { isComponentReady, isDocumentLoaded }
    });
    
    setIsDocumentLoaded(true);
    console.log('✅ [STATE] Document loaded state updated to true');
    
    if (props.onDocumentLoaded) {
      try {
        props.onDocumentLoaded(event);
        console.log('✅ [EVENT] onDocumentLoaded callback executed successfully');
      } catch (error) {
        console.error('❌ [EVENT] Error in onDocumentLoaded callback:', error);
      }
    }
  };

  const handleError = (event: any) => {
    const timestamp = Date.now();
    console.error('📚 [EVENT] Document error:', {
      timestamp,
      nativeEvent: event.nativeEvent,
      previousState: { isComponentReady, isDocumentLoaded }
    });
    
    setIsDocumentLoaded(false);
    console.log('⚠️ [STATE] Document loaded state updated to false due to error');
    
    if (props.onError) {
      try {
        props.onError(event);
        console.log('✅ [EVENT] onError callback executed successfully');
      } catch (error) {
        console.error('❌ [EVENT] Error in onError callback:', error);
      }
    }
  };

  const handlePageChanged = (event: any) => {
    console.log('📚 [EVENT] Page changed:', event.nativeEvent);
    if (props.onPageChanged) {
      try {
        props.onPageChanged(event);
        console.log('✅ [EVENT] onPageChanged callback executed successfully');
      } catch (error) {
        console.error('❌ [EVENT] Error in onPageChanged callback:', error);
      }
    }
  };

  const handleZoomChanged = (event: any) => {
    console.log('📚 [EVENT] Zoom changed:', event.nativeEvent);
    if (props.onZoomChanged) {
      try {
        props.onZoomChanged(event);
        console.log('✅ [EVENT] onZoomChanged callback executed successfully');
      } catch (error) {
        console.error('❌ [EVENT] Error in onZoomChanged callback:', error);
      }
    }
  };

  console.log('📚 FieldcraftDocumentReader rendering with source:', props.source, 'documentType:', props.documentType);
  console.log('📚 Component state - ready:', isComponentReady, 'documentLoaded:', isDocumentLoaded);
  
  try {
    return (
      <NativeFieldcraftDocumentReaderView
        ref={viewRef}
        {...props}
        documentType={props.documentType || 'pdf'}
        pagesPerRequest={5} // Always load 5 pages by default as per library design
        maxZoom={props.maxZoom || 4.0}
        minZoom={props.minZoom || 0.5}
        initialPage={props.initialPage || 1}
        spacing={props.spacing || 10}
        preloadPages={2} // Preload 2 additional pages for smooth scrolling
        maxDisplayWidth={props.maxDisplayWidth || 800}
        systemMargin={props.systemMargin || 16}
        enableWidthFit={props.enableWidthFit || false}
        onDocumentLoaded={handleDocumentLoaded}
        onError={handleError}
        onPageChanged={handlePageChanged}
        onZoomChanged={handleZoomChanged}
        onLoadingStateChanged={props.onLoadingStateChanged}
        onPagesLoaded={props.onPagesLoaded}
        onLoadProgress={props.onLoadProgress}
      />
    );
  } catch (error) {
    console.error('📚 Error rendering FieldcraftDocumentReader:', error);
    return null;
  }
});

FieldcraftDocumentReader.displayName = 'FieldcraftDocumentReader';

export default FieldcraftDocumentReader;